import React, { useEffect, useRef, useState } from 'react'

import { VsInformationIcon } from '@octane/spark'
import searchOutdoorPowerEquipment, {
    OutdoorPowerEquipment,
} from 'api/vehicles/searchOutdoorPowerEquipment'
import { FormikCombobox } from 'components/FormikFields'
import { FormikComboboxChild } from 'components/FormikFields/FormikCombobox/FormikCombobox'
import { constructVehicleDropdownValue } from 'containers/VehicleSelection/dropdowns/VehicleDropdown'
import { OpeUnitValues } from 'form/fields/opeEquipmentFields'
import useDebouncedValue from 'hooks/useDebouncedValue'
import { useTheme } from 'styled-components'
import { formatToRoundedDollar } from 'utils/formatToRoundedDollar'

import {
    EquipmentNotice,
    EquipmentNoticeText,
    Root,
} from './VehicleSearchSelection.styled'

interface VehicleSearchSelectionProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik: any
    unit?: Partial<OpeUnitValues>
    unitPath?: string
    prefilled?: boolean
    ariaLabel?: string
    showNotice?: boolean
    isPrimaryUnit?: boolean
}

const defaultProps = {
    ariaLabel: undefined,
    prefilled: false,
    showNotice: true,
    isPrimaryUnit: true,
    unit: undefined,
    unitPath: 'vehicle.primary',
}

const FIELD_NAME = 'vehicle.primary.selectedVehicleId'
const MIN_SEARCH_LENGTH = 3
const SEARCH_DEBOUNCE_MS = 600

function getEmptyStateCopy({
    hasSearchError,
    queryLength,
}: {
    hasSearchError: boolean
    queryLength: number
}) {
    if (hasSearchError) {
        return {
            title: 'Unable to load equipment',
            message: 'Please check your connection and try your search again.',
        }
    }

    if (queryLength < MIN_SEARCH_LENGTH) {
        return {
            title: 'Start typing to search',
            message: 'Type at least 3 letters or numbers to search.',
        }
    }

    return {
        title: 'No equipment found',
        message: 'Try a different make, model, SKU, or equipment type.',
    }
}

function normalizeSearchQuery(query: string) {
    return query
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
}

function equipmentDisplayName(equipment: OutdoorPowerEquipment) {
    const skuPrefix = equipment.sku ? `${equipment.sku} · ` : ''

    return `${skuPrefix}${equipment.make} ${equipment.model}`
}

function unitDisplayName(unit: Partial<OpeUnitValues>) {
    if (!unit.make && !unit.model) {
        return ''
    }

    const skuPrefix = unit.sku ? `${unit.sku} · ` : ''

    return `${skuPrefix}${unit.make ?? ''} ${unit.model ?? ''}`.trim()
}

export default function VehicleSearchSelection({
    formik,
    unit,
    unitPath = 'vehicle.primary',
    prefilled,
    ariaLabel,
    showNotice = true,
    isPrimaryUnit = true,
}: Readonly<VehicleSearchSelectionProps>) {
    const theme = useTheme()
    const { condition, primary } = formik.values.vehicle
    const selectedUnit = unit ?? primary
    const { selectedVehicleId } = selectedUnit
    const fieldName = unitPath ? `${unitPath}.selectedVehicleId` : FIELD_NAME
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<OutdoorPowerEquipment[]>([])
    const [searchError, setSearchError] = useState(false)
    // The query that `results` was fetched for. Comparing this to
    // `debouncedQuery` (rather than tracking a separate `loading` flag set
    // from inside the search effect) lets `loading` be derived synchronously
    // during render, with no gap between the debounce catching up and the
    // effect actually starting the next fetch — otherwise that gap briefly
    // re-exposes the previous (stale) results between searches.
    const [resultsQuery, setResultsQuery] = useState('')

    const [knownEquipmentByValue, setKnownEquipmentByValue] = useState<
        Record<string, OutdoorPowerEquipment>
    >({})

    const latestSearchRequestId = useRef(0)
    const trimmedQuery = normalizeSearchQuery(query)
    const debouncedQuery = useDebouncedValue(trimmedQuery, SEARCH_DEBOUNCE_MS)

    // True while the debounce is still catching up to the latest keystroke,
    // or while the fetch for the debounced query hasn't resolved yet.
    const loading =
        trimmedQuery.length >= MIN_SEARCH_LENGTH &&
        (trimmedQuery !== debouncedQuery || debouncedQuery !== resultsQuery)

    const rememberEquipment = (equipment: OutdoorPowerEquipment[]) => {
        setKnownEquipmentByValue((previous) => ({
            ...previous,
            ...Object.fromEntries(
                equipment.map((item) => [
                    constructVehicleDropdownValue({
                        id: item.id,
                        vehicleType: item.equipmentType,
                        uuid: item.uuid,
                    }),
                    item,
                ]),
            ),
        }))
    }

    useEffect(() => {
        const abortController = new AbortController()
        const requestId = latestSearchRequestId.current + 1

        latestSearchRequestId.current = requestId

        if (debouncedQuery.length < MIN_SEARCH_LENGTH) {
            setResults([])
            setResultsQuery(debouncedQuery)
            setSearchError(false)

            return undefined
        }

        setSearchError(false)

        searchOutdoorPowerEquipment(
            { query: debouncedQuery, condition },
            abortController,
        )
            .then((response) => {
                if (
                    abortController.signal.aborted ||
                    requestId !== latestSearchRequestId.current
                ) {
                    return
                }

                setResults(response)
                setResultsQuery(debouncedQuery)
                setSearchError(false)
                rememberEquipment(response)
            })
            .catch(() => {
                // A genuine fetch failure still needs to mark this query as
                // settled so `loading` clears - only an aborted (superseded)
                // request should leave it pending for the next effect run.
                if (
                    !abortController.signal.aborted &&
                    requestId === latestSearchRequestId.current
                ) {
                    setResults([])
                    setResultsQuery(debouncedQuery)
                    setSearchError(true)
                }
            })

        return () => abortController.abort()
    }, [debouncedQuery, condition])

    const children: FormikComboboxChild[] =
        trimmedQuery.length < MIN_SEARCH_LENGTH
            ? []
            : results.map((equipment) => ({
                  name: equipmentDisplayName(equipment),
                  value: constructVehicleDropdownValue({
                      id: equipment.id,
                      vehicleType: equipment.equipmentType,
                      uuid: equipment.uuid,
                  }),
                  meta: `MSRP ${formatToRoundedDollar(equipment.msrp)} · ${equipment.equipmentSubTypeLabel}`,
              }))

    const hasSearchError =
        searchError && trimmedQuery.length >= MIN_SEARCH_LENGTH

    const emptyStateCopy = getEmptyStateCopy({
        hasSearchError,
        queryLength: trimmedQuery.length,
    })

    const clearSelectedEquipmentFields = () => {
        if (isPrimaryUnit) {
            formik.setFieldValue('vehicle.vehicleType', '')
            formik.setFieldValue('vehicle.isTrailer', false)
        }

        formik.setFieldValue(`${unitPath}.year`, '')
        formik.setFieldValue(`${unitPath}.make`, '')
        formik.setFieldValue(`${unitPath}.model`, '')
        formik.setFieldValue(`${unitPath}.msrp`, '')
        formik.setFieldValue(`${unitPath}.asset_value`, '')
        formik.setFieldValue(`${unitPath}.equipmentType`, '')
        formik.setFieldValue(`${unitPath}.equipmentSubType`, '')
        formik.setFieldValue(`${unitPath}.sku`, '')
    }

    const setSelectedEquipmentFields = (
        equipmentType: string,
        selected: OutdoorPowerEquipment | undefined,
    ) => {
        if (isPrimaryUnit) {
            formik.setFieldValue('vehicle.vehicleType', equipmentType)
            // OPE's equipment list never includes trailer types.
            formik.setFieldValue('vehicle.isTrailer', false)
        }

        formik.setFieldValue(
            `${unitPath}.year`,
            selected ? String(selected.year) : '',
        )

        formik.setFieldValue(`${unitPath}.make`, selected?.make ?? '')
        formik.setFieldValue(`${unitPath}.model`, selected?.model ?? '')
        formik.setFieldValue(`${unitPath}.msrp`, selected?.msrp ?? '')

        formik.setFieldValue(`${unitPath}.asset_value`, selected?.msrp ?? '')

        formik.setFieldValue(
            `${unitPath}.equipmentType`,
            selected?.equipmentType ?? equipmentType,
        )

        formik.setFieldValue(
            `${unitPath}.equipmentSubType`,
            selected?.equipmentSubType ?? '',
        )

        formik.setFieldValue(`${unitPath}.sku`, selected?.sku ?? '')
    }

    const searchSelectedEquipmentBySku = () => {
        if (selectedVehicleId && selectedUnit.sku) {
            setQuery(selectedUnit.sku)
        }
    }

    return (
        <Root>
            {showNotice && (
                <EquipmentNotice>
                    <VsInformationIcon
                        color={theme.colors.alert.note}
                        width={18}
                        height={18}
                    />
                    <EquipmentNoticeText>
                        Our financing program covers new equipment only. All
                        equipment must be from the same OEM for this deal.
                    </EquipmentNoticeText>
                </EquipmentNotice>
            )}
            <FormikCombobox
                name={fieldName}
                ariaLabel={ariaLabel}
                placeholder="Search Make, Model, SKU, Equipment Type or Sub Type"
                error={!selectedVehicleId}
                query={query}
                onQueryChange={setQuery}
                onFocus={searchSelectedEquipmentBySku}
                loading={loading}
                emptyTitle={emptyStateCopy.title}
                displayValue={(value: string) => {
                    if (value && value === selectedVehicleId) {
                        const label = unitDisplayName(selectedUnit)

                        if (label) {
                            return label
                        }
                    }

                    const equipment = knownEquipmentByValue[value]

                    return equipment ? equipmentDisplayName(equipment) : ''
                }}
                onChange={(value: string) => {
                    if (!value) {
                        clearSelectedEquipmentFields()

                        return
                    }

                    const [, equipmentType] = value.split(':')
                    const selected = knownEquipmentByValue[value]

                    setSelectedEquipmentFields(equipmentType, selected)
                }}
                onClear={clearSelectedEquipmentFields}
                emptyMessage={emptyStateCopy.message}
                prefilled={prefilled}
            >
                {children}
            </FormikCombobox>
        </Root>
    )
}

VehicleSearchSelection.defaultProps = defaultProps

export const testExports = {
    equipmentDisplayName,
    getEmptyStateCopy,
    normalizeSearchQuery,
    unitDisplayName,
}
